<script setup lang="ts">
import { dialogEvents } from "@/data-views/dialogs";
import { createMediaStore, Media } from "@/data/entities/media";
import { DocRef } from "@/data/general/doc_ref";
import { VFileInput } from "vuetify/lib/components/index.mjs";
import { FormInputMedia } from "../types";
import { toast } from "vue3-toastify";
import { createFormStore } from "../form.store";

/////////////////////////////////////////////////////////////
//////////////////////// PROPS //////////////////////////////
/////////////////////////////////////////////////////////////
const props = defineProps<{
  config: FormInputMedia;
  modelValue: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();
/////////////////////////////////////////////////////////////
////////////////////// SETUP /////////////////////////////
/////////////////////////////////////////////////////////////
const { t } = useI18n();
const mediaStore = createMediaStore();
const formStore = createFormStore();

/////////////////////////////////////////////////////////////
////////////////////// VARIABLES /////////////////////////////
/////////////////////////////////////////////////////////////
const files = ref<File[]>();
const loading = ref(false);
const url = ref("");
const refFileInput = ref<VFileInput>();
const media = ref<Media>(new Media());
const dimensions = ref({ width: 0, height: 0 });
const deleting = ref(false);
const bytesSrc = ref<any>(null);

/////////////////////////////////////////////////////////////
////////////////////// COMPUTED /////////////////////////////
/////////////////////////////////////////////////////////////
const id = computed({
  get: () => props.modelValue,
  set: (value: string) => {
    // Emit the ID
    emit("update:modelValue", value);

    // Set all other form values
    setMedia();
  },
});

/////////////////////////////////////////////////////////////
////////////////////// METHODS //////////////////////////////
/////////////////////////////////////////////////////////////
async function loadMedia() {
  loading.value = true;

  if (!id.value) {
    loading.value = false;
    return;
  }

  // Retrieve the item from the store
  media.value = await mediaStore.retrieve(id.value).catch((error: Error) => {
    console.error(error);
    return new Media();
  });

  // Get media
  if (media.value) {
    // Set media
    setMedia();

    // Get image size
    if (media.value.mimeType.includes("image")) {
      dimensions.value = await media.value.getImageSize();
    }

    // Set url
    url.value = media.value.url;

    if (media.value.mimeType === "") {
      const response = await fetch(media.value.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = () => {
        bytesSrc.value = reader.result;
      };
      reader.readAsDataURL(blob);
    }
  }

  // Set media URL
  loading.value = false;
}

async function createMedia(file: File) {
  loading.value = true;

  // Remove url
  url.value = "";

  // Create media
  const media = await Media.Create(file).catch((error: Error) => {
    console.error(error);
    toast.error(t(error.message));
    throw error;
  });

  // Set the media ID
  id.value = media.id;

  // Set media URL
  loading.value = false;

  setTimeout(() => {
    void loadMedia();
  }, 1000);
}

function chooseNewFile() {
  refFileInput.value?.click();
}

function editMedia() {
  // Open the media dialog
  dialogEvents.MediaFormDialog.open(media.value.id);
}

async function deleteMedia() {
  loading.value = true;
  deleting.value = true;

  // Delete the media
  if (media.value) {
    await mediaStore.delete(media.value as Media);
  }

  // Remove media
  id.value = "";
  url.value = "";
  files.value = [];

  // Set media URL
  loading.value = false;

  setTimeout(() => {
    deleting.value = false;
  }, 1000);
}

function setMedia(): void {
  const path = props.config.name.replace(".id", "");
  Object.keys(media.value.toJSON()).forEach((key) => {
    if (key !== "id") {
      formStore.set(`${path}.${key}`, (media.value as any)[key]);
    }
  });
}

function downloadMedia() {
  if (media.value) {
    window.open(media.value.url, "_blank");
  }
}

/////////////////////////////////////////////////////////////
////////////////////// WATCHERS /////////////////////////////
/////////////////////////////////////////////////////////////
watch(files, (value: File[] | undefined) => {
  if (value?.length && value.length > 0) {
    createMedia(value[0]);
  }
});

watch(
  () => props.modelValue,
  () => {
    if (!deleting.value) {
      loadMedia();
    }
  },
  {
    deep: true,
  },
);

/////////////////////////////////////////////////////////////
////////////////////// HOOKS ////////////////////////////////
/////////////////////////////////////////////////////////////
onMounted(() => {
  loadMedia();
  window.addEventListener("store.update.Media", async () => {
    loadMedia();
  });
});
</script>

<template>
  <VRow v-if="config.type === 'avatar'">
    <VCol cols="12">
      <VAvatar
        :loading="loading"
        size="150"
        class="cursor-pointer"
        color="primary"
        variant="tonal"
        @click="chooseNewFile"
        style="position: relative"
      >
        <div v-if="loading" class="loader"></div>
        <VImg
          v-else-if="url"
          :src="url"
          style="width: 100%; height: 100%"
          cover
        />
        <VIcon v-else icon="tabler-user" />
        <VFileInput
          ref="refFileInput"
          v-model="files"
          :disabled="disabled"
          :loading="loading"
          color="primary"
          label="File input"
          variant="outlined"
          :multiple="false"
          :accept="config.accepts"
          style="display: none"
        />
      </VAvatar>
    </VCol>
    <VCol cols="12"> </VCol>
  </VRow>

  <VRow v-else>
    <VCol v-if="url" :cols="12">
      <VCard>
        <div class="d-flex flex-column-reverse flex-md-row">
          <VRow>
            <VCol :cols="6">
              <VRow class="pl-6 pt-6">
                <VCol>
                  <VIcon class="mr-2">tabler-photo</VIcon>
                  {{ media.extension.toUpperCase() }}
                </VCol>
                <VCol v-if="dimensions.width > 0 && dimensions.height > 0">
                  <VIcon class="mr-2">tabler-maximize</VIcon>
                  {{ dimensions.width }} x {{ dimensions.height }}
                </VCol>
              </VRow>
              <VCardText>
                {{ media.description || $t("No description available") }}
              </VCardText>
            </VCol>
            <VCol :cols="6">
              <div class="ma-auto pa-5">
                <VImg
                  v-if="media.mimeType.includes('image')"
                  width="100%"
                  max-height="300"
                  ratio="1"
                  cover
                  :src="url"
                  class="rounded"
                >
                  <template v-slot:placeholder>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-progress-circular
                        color="grey-lighten-4"
                        indeterminate
                      ></v-progress-circular>
                    </div>
                  </template>
                </VImg>

                <iframe
                  v-else
                  width="100%"
                  height="300"
                  :src="media.mimeType === '' ? bytesSrc : url"
                  frameborder="0"
                  allowfullscreen
                ></iframe>
              </div>
            </VCol>
          </VRow>

          <VCardActions style="position: absolute; bottom: 0 !important">
            <VBtn
              :disabled="disabled || config.disabled"
              color="primary"
              @click="editMedia"
            >
              <VIcon class="mr-2">tabler-edit</VIcon>
              {{ $t("Edit") }}
            </VBtn>
            <VBtn
              :disabled="disabled || config.disabled"
              color="default"
              @click="chooseNewFile"
            >
              <VIcon class="mr-2">tabler-upload</VIcon>
              {{ $t("Change") }}
            </VBtn>
            <VBtn
              :disabled="disabled || config.disabled"
              color="error"
              @click="deleteMedia"
            >
              <VIcon class="mr-2">tabler-trash</VIcon>
              {{ $t("Delete") }}
            </VBtn>
            <VBtn v-if="media.url" color="default" @click="downloadMedia">
              <VIcon class="mr-2">tabler-download</VIcon>
              {{ $t("Download") }}
            </VBtn>
          </VCardActions>
        </div>
      </VCard>
    </VCol>
    <VCol v-show="!url" :cols="12">
      <VFileInput
        ref="refFileInput"
        v-model="files"
        :disabled="disabled"
        :loading="loading"
        color="primary"
        :label="$t('File input')"
        variant="outlined"
        :multiple="false"
        :accept="config.accepts"
      />
    </VCol>
  </VRow>
</template>
